import { useEffect, useRef, TransitionEvent, useState } from 'react';
import classNames from 'classnames';

import { IS_DISCORD_ACTIVITY } from '@/globals';
import { useTheme } from '@/theme';
import { getAvailableBadges, getAvailableTitles, loadCurrentUserProfileV2 } from '@/state/features/profile';
import {
    isDiscordOnMobile,
    shareImageInDiscordDesktop,
    triggerDiscordShare,
} from '@/components/DiscordAuthFlow/useDiscord';
import { useAppDispatch, useAppSelector } from '@/state/store';
import { closeProfile } from '@/state/features/app';
import { useAudio } from '@/hooks/audio';
import IconButton from '@/components/IconButton';
import Icon, { IconTypes } from '@/components/Icon';
import { sendEventV2 } from '@/utils/analytics';
import { pathToURL } from '@/utils/urls';
import { Badge, Title } from '@/models/profile';
import { getTranslatedBadge, getTranslatedTitle, useUITranslatedText } from '@/components/TranslatedText';

import LevelBadge from './LevelBadge';
import RoundProgressBar from './RoundProgressBar';
import ShinyLoader from './ShinyLoader';
import TitleBadge from './Title';
import getProfileV2ShareImage from './imageMaker';
import * as styles from './styles.module.scss';

export default function ProfileDrawerV2() {
    const dispatch = useAppDispatch();
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const { audio } = useAudio();
    const theme = useTheme();

    const isProfileOpen = useAppSelector((state) => state.app.isProfileOpen);
    const { currentUserProfileV2, availableBadges, availableTitles } = useAppSelector((state) => state.profile);
    const loggedIn = useAppSelector((state) => state.auth.loggedIn);

    const brandingData = theme.metadata?.customJSON?.profileDrawerV2;
    const shareText = theme.metadata?.customJSON?.share?.text;

    const isDiscordBranded = IS_DISCORD_ACTIVITY;

    // Translations
    const translation_quest = useUITranslatedText('profileDrawer_quest', 'Quest');
    const translation_mission = useUITranslatedText('profileDrawer_mission', 'Mission');
    const translation_logoImageUrl = useUITranslatedText(
        'profileDrawer_logoImage',
        pathToURL(brandingData?.logoImagePath),
    );

    useEffect(() => {
        if (isProfileOpen) {
            audio?.play('gust-open');
            sendEventV2('open_profile');
        }
    }, [isProfileOpen]);

    // Move focus to drawer when opened
    function onTransitionEnd(e: TransitionEvent<HTMLDivElement>) {
        if (isProfileOpen && e.target === e.currentTarget) {
            setTimeout(() => {
                closeButtonRef.current?.focus();
            }, 200);
        }
    }

    useEffect(() => {
        // Load data initially
        dispatch(getAvailableBadges());
        dispatch(getAvailableTitles());
    }, [loggedIn]);

    useEffect(() => {
        // Load data initially, and then again once user is logged in
        dispatch(loadCurrentUserProfileV2());
    }, [loggedIn]);

    return (
        <div
            className={classNames(styles.ProfileDrawer, {
                [styles['isOpen']]: isProfileOpen,
            })}
            inert={!isProfileOpen ? '' : undefined}
            onTransitionEnd={onTransitionEnd}
        >
            <IconButton
                buttonRef={closeButtonRef}
                iconType="close"
                buttonStyle="round"
                ariaLabel="Close Profile"
                className={styles.closeButton}
                onClick={() => {
                    audio?.play('gust-close');
                    sendEventV2('close_profile');
                    dispatch(closeProfile());
                }}
            />
            <ProfileDrawerV2Contents
                isVisible={isProfileOpen}
                availableTitles={availableTitles?.map((title) => getTranslatedTitle(title))}
                usersTitles={currentUserProfileV2?.titles?.map((title) => getTranslatedTitle(title))}
                availableBadges={availableBadges?.map((title) => getTranslatedBadge(title))}
                usersBadges={currentUserProfileV2?.badges?.map((title) => getTranslatedBadge(title))}
                hideGameCompletionUI={brandingData?.hideGameCompletionUI}
                isGameCompleted={currentUserProfileV2?.missionCompleted}
                username={currentUserProfileV2?.displayName}
                brandCardImageUrl={pathToURL(brandingData?.cardImagePath)}
                brandLogoImageUrl={translation_logoImageUrl}
                brandBackgroundImageUrl={pathToURL(brandingData?.backgroundImagePath)}
                platformIcon={isDiscordBranded ? 'discord' : 'profile'}
                gameDescriptionWord={isDiscordBranded ? translation_quest : translation_mission}
                gameIcon={isDiscordBranded ? 'wreath' : 'action'}
                hideBadgesLine={brandingData?.hideBadgesLine}
                shareText={shareText}
            />
        </div>
    );
}

interface ProfileDrawerV2ContentsProps {
    isVisible?: boolean; // For triggering animations on open
    availableTitles: Title[];
    usersTitles: Title[];
    availableBadges: Badge[];
    usersBadges: Badge[];
    username: string;
    brandCardImageUrl: string;
    brandLogoImageUrl: string;
    brandBackgroundImageUrl: string;
    hideGameCompletionUI?: boolean; // e.g hides 'Quest complete' text
    isGameCompleted?: boolean;
    platformIcon: IconTypes; // 'wreath'
    gameDescriptionWord: string; // 'Mission'
    gameIcon: IconTypes; // 'discord'
    hideBadgesLine?: boolean; // Hides the line behind the badges
    shareText: string; // Used when sharing on discord mobile as image sharing not supported
}

function ProfileDrawerV2Contents({
    isVisible,
    availableTitles,
    usersTitles,
    availableBadges,
    usersBadges,
    username,
    brandCardImageUrl,
    brandLogoImageUrl,
    brandBackgroundImageUrl,
    hideGameCompletionUI,
    isGameCompleted,
    platformIcon,
    gameDescriptionWord,
    gameIcon,
    hideBadgesLine,
    shareText,
}: ProfileDrawerV2ContentsProps) {
    const [currentTab, setCurrentTab] = useState<'levels' | 'titles'>('levels');
    const [isShareError, setIsShareError] = useState(false);
    const [isShareLoading, setIsShareLoading] = useState(false);

    const [revealedHints, setRevealedHints] = useState<Set<string>>(new Set());
    const handleRevealHint = (id: string) => {
        setRevealedHints((prev) => new Set(prev).add(id));
        sendEventV2('profile_view_hint', [id]);
    };

    // Translations
    const translation_scorecard = useUITranslatedText('profileDrawer_scorecard', 'Scorecard');
    const translation_titles = useUITranslatedText('profileDrawer_titles', 'Titles');
    const translation_level = useUITranslatedText('profileDrawer_level', 'Level');
    const translation_yourLevel = useUITranslatedText('profileDrawer_yourLevel', 'Your Level');
    const translation_incomplete = useUITranslatedText('profileDrawer_incomplete', 'Incomplete');
    const translation_complete = useUITranslatedText('profileDrawer_complete', 'Complete');
    const translation_titlesUnlocked = useUITranslatedText('profileDrawer_titlesUnlocked', 'Titles Unlocked');
    const translation_yourStats = useUITranslatedText('profileDrawer_yourStats', 'Your stats');
    const translation_levels = useUITranslatedText('profileDrawer_levels', 'Levels');
    const translation_share = useUITranslatedText('share', 'Share');
    const translation_shareError = useUITranslatedText('profileDrawer_shareError', 'Unable to share, try again later');
    const translation_titleNotFound = useUITranslatedText(
        'profileDrawer_titleNotFound',
        "You haven't found this Title yet!",
    );
    const translation_hint = useUITranslatedText('profileDrawer_hint', 'Hint');

    // Rarity translations
    const translation_rarityCommon = useUITranslatedText('rarity_common', 'Common');
    const translation_rarityUncommon = useUITranslatedText('rarity_uncommon', 'Uncommon');
    const translation_rarityRare = useUITranslatedText('rarity_rare', 'Rare');
    const translation_rarityEpic = useUITranslatedText('rarity_epic', 'Epic');
    const translation_rarityLegendary = useUITranslatedText('rarity_legendary', 'Legendary');

    const rarityTranslations: Record<string, string> = {
        common: translation_rarityCommon,
        uncommon: translation_rarityUncommon,
        rare: translation_rarityRare,
        epic: translation_rarityEpic,
        legendary: translation_rarityLegendary,
    };

    const COMPLETED_COLOR = '#7FBF5A';
    // Current badge is badge with highest order number (e.g. gold with order of 2 is shown over bronze with 0)
    const currentBadge = usersBadges.length ? usersBadges.reduce((a, b) => (b.order > a.order ? b : a)) : null;
    const levelColor = currentBadge?.color ?? 'white';
    const gameStatusText = isGameCompleted ? translation_complete : translation_incomplete;
    const mainTitleText = `${gameDescriptionWord} ${translation_scorecard}`;
    const numberOfCompletedTitles = usersTitles?.length || 0;
    const numberOfAvailableTitles = availableTitles?.length || 0;
    const titlesPercent = (numberOfCompletedTitles / numberOfAvailableTitles) * 100 || 0;

    async function openProfileImage() {
        setIsShareLoading(true);
        setIsShareError(false);
        try {
            const filename = `${gameDescriptionWord} Scorecard.png`;

            // Give 2 extra seconds to allow device sharing functions to load
            const ADDITIONAL_LOADING_TIME_MS = 2000;

            if (IS_DISCORD_ACTIVITY) {
                if (isDiscordOnMobile()) {
                    // Don't share the card on discord mobile as not supported
                    await triggerDiscordShare(shareText);
                    setTimeout(() => {
                        setIsShareLoading(false);
                    }, ADDITIONAL_LOADING_TIME_MS);
                } else {
                    // Discord desktop
                    const imageUrl = await getProfileV2ShareImage(brandBackgroundImageUrl);
                    await shareImageInDiscordDesktop(imageUrl, filename);
                    sendEventV2('share_profile_image', ['discord']);

                    setTimeout(() => {
                        setIsShareLoading(false);
                    }, ADDITIONAL_LOADING_TIME_MS);
                }
            } else {
                // Web
                const imageUrl = await getProfileV2ShareImage(brandBackgroundImageUrl);
                sendEventV2('share_profile_image', ['web']);
                // Does not work in iPhone browser
                window.open(imageUrl, '_blank', 'noopener');

                setTimeout(() => {
                    setIsShareLoading(false);
                }, ADDITIONAL_LOADING_TIME_MS);
            }
        } catch (error) {
            console.log('Error sharing profile:', error);
            setIsShareError(true);
            setIsShareLoading(false);
        }
    }

    return (
        <div className={styles.scrollContainer}>
            <div className={styles.content}>
                <div
                    className={styles.pageBgImage}
                    style={{
                        backgroundImage: `url("${brandBackgroundImageUrl}")`,
                    }}
                ></div>
                <section className={styles.scorecard} id="arise-profile-scorecard">
                    <div className={styles.scorecardContent}>
                        <div
                            className={styles.bgImage}
                            style={{
                                backgroundImage: `url("${brandCardImageUrl}")`,
                            }}
                        ></div>
                        <div className={styles.divider} />
                        <div className={styles.topBar}>
                            {brandLogoImageUrl && (
                                <img src={brandLogoImageUrl} className={styles.brandLogo} crossOrigin="anonymous" />
                            )}
                            <Icon className={styles.platformLogo} type={platformIcon} />
                        </div>
                        <div className={styles.titleBar}>
                            <h2
                                className={classNames({
                                    [styles.isLong]: mainTitleText?.length > 18,
                                    [styles.isExtraLong]: mainTitleText?.length > 25,
                                })}
                            >
                                {mainTitleText}
                            </h2>
                            {username && (
                                <div className={styles.username}>
                                    <Icon className={styles.icon} type={platformIcon} />
                                    <span>{username}</span>
                                </div>
                            )}
                        </div>
                        <div className={styles.divider} />
                        <ul className={styles.statBoxContainer}>
                            {!hideGameCompletionUI && (
                                <li className={styles.statBox}>
                                    <ShinyLoader isLoaded={typeof isGameCompleted === 'boolean'} />
                                    <Icon className={styles.icon} type={gameIcon} />
                                    <div className={styles.statTextContainer}>
                                        <span className={styles.statTitle}>{gameDescriptionWord}</span>
                                        <span className={styles.subtitle}>{gameStatusText}</span>
                                    </div>
                                </li>
                            )}
                            <li className={styles.statBox}>
                                <ShinyLoader
                                    isLoaded={
                                        typeof numberOfCompletedTitles === 'number' &&
                                        typeof numberOfAvailableTitles === 'number'
                                    }
                                />
                                <Icon className={styles.icon} type="crown" />
                                <div className={styles.statTextContainer}>
                                    <span className={styles.statTitle}>
                                        {numberOfCompletedTitles}/{numberOfAvailableTitles}
                                    </span>
                                    <span className={styles.subtitle}>{translation_titles}</span>
                                </div>
                            </li>
                            <li className={classNames(styles.statBox, styles.statBoxLong)}>
                                <ShinyLoader isLoaded={!!usersBadges} />
                                <Icon
                                    className={classNames(styles.icon, styles.active)}
                                    type="shield"
                                    style={{ fill: levelColor }}
                                />
                                <div className={styles.statTextContainer}>
                                    <span className={styles.subtitle}>{translation_level}</span>
                                    <span className={styles.statTitle} style={{ color: levelColor }}>
                                        {currentBadge?.name ?? translation_incomplete}
                                    </span>
                                </div>
                            </li>
                            <LevelBadge
                                classname={styles.levelBadge}
                                imagePath={currentBadge?.imagePath}
                                isLocked={!currentBadge}
                                isLoading={!usersBadges}
                                shimmerDelay={3}
                            />
                        </ul>
                        <div className={styles.divider} />
                    </div>
                </section>
                <div className={styles.ctaButtonsContainer}>
                    <button onClick={openProfileImage} disabled={isShareLoading}>
                        {translation_share}
                        <Icon type={isShareLoading ? 'animated-spinner' : 'share'} />
                    </button>
                    {isShareError && <p className={styles.shareError}>{translation_shareError}</p>}
                </div>
                <section className={styles.yourStats}>
                    <h2>
                        <Icon className={classNames(styles.icon)} type="bolt" />
                        {translation_yourStats}
                    </h2>
                    <div className={styles.gridContainer}>
                        <div className={classNames(styles.level, styles.box)}>
                            <ShinyLoader isLoaded={!!usersBadges} />
                            <LevelBadge
                                classname={styles.levelBadge}
                                imagePath={currentBadge?.imagePath}
                                isLoading={!usersBadges}
                                shimmerDelay={4}
                                isLocked={!currentBadge}
                            />
                            <span className={styles.statTitle} style={{ color: levelColor }}>
                                {currentBadge?.name ?? translation_incomplete}
                            </span>
                            <span className={styles.subtitle}>{translation_yourLevel}</span>
                        </div>
                        {!hideGameCompletionUI && (
                            <div className={classNames(styles.statBox, styles.box)}>
                                <ShinyLoader isLoaded={typeof isGameCompleted === 'boolean'} />
                                <div className={styles.left}>
                                    <Icon
                                        className={classNames(styles.icon, styles.active)}
                                        type={gameIcon}
                                        style={{ fill: isGameCompleted ? COMPLETED_COLOR : '#BF5A5A' }}
                                    />
                                </div>
                                <div>
                                    <span className={styles.statTitle}>{gameDescriptionWord}</span>
                                    <span className={styles.subtitle}>{gameStatusText}</span>
                                </div>
                            </div>
                        )}
                        <div className={classNames(styles.statBox, styles.box)}>
                            <ShinyLoader
                                isLoaded={
                                    typeof numberOfCompletedTitles === 'number' &&
                                    typeof numberOfAvailableTitles === 'number'
                                }
                            />
                            <div className={styles.left}>
                                <RoundProgressBar
                                    percent={isVisible ? titlesPercent : 100}
                                    className={styles.progress}
                                    icon="crown"
                                />
                            </div>
                            <div>
                                <span className={styles.titlesNumber}>
                                    <span className={styles.highlight}>{numberOfCompletedTitles}</span>
                                    <span>/{numberOfAvailableTitles}</span>
                                </span>
                                <span className={styles.subtitle}>{translation_titlesUnlocked}</span>
                            </div>
                        </div>
                    </div>
                </section>
                <section className={styles.tabGroup}>
                    <div className={styles.tabButtons} role="tablist" aria-label="Stat Information Options">
                        <button
                            className={classNames({ [styles.active]: currentTab === 'levels' })}
                            role="tab"
                            aria-selected={currentTab === 'levels'}
                            disabled={currentTab === 'levels'}
                            aria-controls="panel-levels"
                            id="tab-levels"
                            onClick={() => {
                                setCurrentTab('levels');
                                sendEventV2('view_profile_section', ['levels']);
                            }}
                        >
                            <Icon type="shield" />
                            {translation_levels}
                        </button>
                        <button
                            className={classNames({ [styles.active]: currentTab === 'titles' })}
                            role="tab"
                            aria-selected={currentTab === 'titles'}
                            disabled={currentTab === 'titles'}
                            aria-controls="panel-titles"
                            id="tab-titles"
                            onClick={() => {
                                setCurrentTab('titles');
                                sendEventV2('view_profile_section', ['titles']);
                            }}
                        >
                            <Icon type="crown" />
                            {translation_titles}
                        </button>
                    </div>
                    {currentTab === 'levels' && (
                        <section id="panel-levels" role="tabpanel" aria-labelledby="tab-levels">
                            <ul className={styles.levelInfoBoxes}>
                                <ShinyLoader
                                    isLoaded={availableBadges && !!usersBadges}
                                    className={styles.tabGroupLoader}
                                />
                                {availableBadges &&
                                    usersBadges &&
                                    availableBadges?.map((badge: Badge, index) => {
                                        const badgeIsCompleted = usersBadges?.some(
                                            (userBadge) => userBadge.id === badge.id,
                                        );
                                        return (
                                            <li
                                                key={badge.id}
                                                className={classNames(styles.levelInfoBox, {
                                                    [styles.hasLine]: !hideBadgesLine,
                                                })}
                                            >
                                                <LevelBadge
                                                    classname={styles.levelBadge}
                                                    imagePath={badge.imagePath}
                                                    isLoading={!usersBadges}
                                                    shimmerDelay={index}
                                                    isLocked={!badgeIsCompleted}
                                                />
                                                <div className={styles.textContainer}>
                                                    <span className={styles.largerText} style={{ color: badge.color }}>
                                                        {badge.name}
                                                    </span>
                                                    <span className={styles.subtitle}>{badge.description}</span>
                                                </div>
                                                <Icon
                                                    className={styles.icon}
                                                    type="checked-box"
                                                    style={{
                                                        fill: badgeIsCompleted ? COMPLETED_COLOR : '#4E4E4E',
                                                    }}
                                                />
                                            </li>
                                        );
                                    })}
                            </ul>
                        </section>
                    )}
                    {currentTab === 'titles' && (
                        <section id="panel-titles" role="tabpanel" aria-labelledby="tab-titles">
                            <ul className={styles.titles}>
                                <ShinyLoader isLoaded={!!availableTitles} className={styles.tabGroupLoader} />
                                {availableTitles?.map((title: Title) => {
                                    const titleIsCompleted = usersTitles?.some(
                                        (userBadge) => userBadge.id === title.id,
                                    );
                                    return (
                                        <li key={title.id}>
                                            {titleIsCompleted ? (
                                                <>
                                                    <div className={styles.left}>
                                                        <TitleBadge text={title.title} rarity={title.rarity} />
                                                    </div>
                                                    <span className={styles.subtitle}>
                                                        <span className={styles[title.rarity?.toLowerCase()]}>
                                                            {rarityTranslations[title.rarity?.toLowerCase()] ||
                                                                title.rarity}
                                                            :{' '}
                                                        </span>
                                                        <span>{title.description}</span>
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <div className={styles.left}>
                                                        <Icon className={styles.lockedIcon} type="locked" />
                                                    </div>
                                                    <span className={styles.subtitle}>
                                                        {revealedHints.has(title.id)
                                                            ? title.hint
                                                            : translation_titleNotFound}
                                                    </span>
                                                    {!revealedHints.has(title.id) && (
                                                        <button
                                                            onClick={() => handleRevealHint(title.id)}
                                                            className={styles.hintButton}
                                                        >
                                                            {translation_hint}
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </section>
                    )}
                </section>
            </div>
        </div>
    );
}
